import * as THREE from 'three'
import { GravityWell } from './gravity-well'

export type ParticleShape = 'glow' | 'heart' | 'star'
export type RingShape = 'circle' | 'ellipse' | 'diamond'

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  captured: boolean
  fadeTimer: number
}

// Debug metrics (exported for overlay)
export interface ParticleMetrics {
  particleCount: number
  maxVelocity: number
  avgVelocity: number
  zoneDistribution: {
    far: number
    orbit: number
    disk: number
    core: number
  }
}

export class ParticleSystem {
  private scene: THREE.Scene
  private particles: Particle[] = []
  private geometry: THREE.BufferGeometry
  private material: THREE.PointsMaterial
  private points: THREE.Points

  private readonly PARTICLE_COUNT: number
  private readonly SPAWN_MARGIN = 200

  // Physics constants (PRD v2)
  private readonly GRAVITY_STRENGTH = 55.0 // Tuned for Plummer softening
  private readonly DAMPING_PER_SEC = 0.6 // Frame-rate independent damping
  private readonly BASE_MAX_VELOCITY = 40 // Will be scaled by R

  // Zone constants (relative to surfaceRadius R)
  private readonly CORE_RADIUS_FACTOR = 0.35
  private readonly DISK_WIDTH_FACTOR = 0.18 // Narrow disk band
  private readonly ORBIT_OUTER_FACTOR = 2.5
  // FAR_OUTER_FACTOR = 6.0 (used for visual effects in Stage 5)

  // Tangential force (orbit zone)
  private readonly ORBIT_OMEGA = 0.15 // Tangential speed - gentle curve, gravity still wins

  private driftMode = false
  private driftAngle = 0
  private lastTime = 0

  // Settings
  private ringShape: RingShape = 'circle'

  // Metrics for debug overlay
  private currentMaxVelocity = 0
  private currentAvgVelocity = 0
  private zoneCount = { far: 0, orbit: 0, disk: 0, core: 0 }

  constructor(scene: THREE.Scene, particleShape: ParticleShape = 'glow', ringShape: RingShape = 'circle') {
    this.scene = scene
    this.ringShape = ringShape
    this.PARTICLE_COUNT = this.getOptimalParticleCount()
    this.initParticles()

    this.geometry = new THREE.BufferGeometry()
    this.updateGeometry()

    const texture = this.createTexture(particleShape)

    this.material = new THREE.PointsMaterial({
      size: 38, // Base size (50% larger), will be adjusted per-particle via scale
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      map: texture,
      depthWrite: false
    })

    this.points = new THREE.Points(this.geometry, this.material)
    this.scene.add(this.points)

    this.lastTime = performance.now() / 1000
  }

  private createTexture(shape: ParticleShape): THREE.Texture {
    switch (shape) {
      case 'heart':
        return this.createHeartTexture()
      case 'star':
        return this.createStarTexture()
      default:
        return this.createGlowTexture()
    }
  }

  private createGlowTexture(): THREE.Texture {
    const size = 64
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!

    const gradient = ctx.createRadialGradient(
      size / 2, size / 2, 0,
      size / 2, size / 2, size / 2
    )
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)')
    gradient.addColorStop(0.2, 'rgba(255, 255, 255, 0.8)')
    gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.4)')
    gradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.1)')
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')

    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, size, size)

    const texture = new THREE.CanvasTexture(canvas)
    texture.needsUpdate = true
    return texture
  }

  private createHeartTexture(): THREE.Texture {
    const size = 64
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!

    const cx = size / 2
    const cy = size / 2
    const scale = size / 64

    // Draw outer glow first
    const glowGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2)
    glowGradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)')
    glowGradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.2)')
    glowGradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.05)')
    glowGradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
    ctx.fillStyle = glowGradient
    ctx.fillRect(0, 0, size, size)

    // Draw heart shape
    ctx.beginPath()
    ctx.moveTo(cx, cy + 10 * scale)
    // Left curve
    ctx.bezierCurveTo(
      cx - 18 * scale, cy - 3 * scale,
      cx - 18 * scale, cy - 16 * scale,
      cx, cy - 10 * scale
    )
    // Right curve
    ctx.bezierCurveTo(
      cx + 18 * scale, cy - 16 * scale,
      cx + 18 * scale, cy - 3 * scale,
      cx, cy + 10 * scale
    )
    ctx.closePath()

    // Solid gradient fill for heart
    const heartGradient = ctx.createRadialGradient(cx, cy - 5 * scale, 0, cx, cy, 20 * scale)
    heartGradient.addColorStop(0, 'rgba(255, 255, 255, 1)')
    heartGradient.addColorStop(0.6, 'rgba(255, 255, 255, 0.9)')
    heartGradient.addColorStop(1, 'rgba(255, 255, 255, 0.7)')

    ctx.fillStyle = heartGradient
    ctx.fill()

    const texture = new THREE.CanvasTexture(canvas)
    texture.needsUpdate = true
    return texture
  }

  // Calculate target ring radius based on angle and ring shape
  private getShapedRadius(baseR: number, angle: number): number {
    switch (this.ringShape) {
      case 'ellipse':
        // Ellipse: wider horizontally (1.4x), narrower vertically (0.7x)
        return baseR * (1 + 0.35 * Math.cos(2 * angle))
      case 'diamond':
        // Diamond/square: use absolute value of sin+cos for 4 corners
        const absAngle = Math.abs(Math.cos(angle)) + Math.abs(Math.sin(angle))
        // Normalize to keep average radius close to R
        return baseR * (absAngle / 1.414)
      default:
        return baseR
    }
  }

  private createStarTexture(): THREE.Texture {
    const size = 64
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!

    const cx = size / 2
    const cy = size / 2
    const outerRadius = size / 2 - 8
    const innerRadius = outerRadius * 0.4
    const points = 5

    // Draw outer glow first
    const glowGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2)
    glowGradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)')
    glowGradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.2)')
    glowGradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.05)')
    glowGradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
    ctx.fillStyle = glowGradient
    ctx.fillRect(0, 0, size, size)

    // Draw 5-point star
    ctx.beginPath()
    for (let i = 0; i < points * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius
      const angle = (i * Math.PI) / points - Math.PI / 2
      const x = cx + Math.cos(angle) * radius
      const y = cy + Math.sin(angle) * radius
      if (i === 0) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.closePath()

    // Solid gradient fill for star
    const starGradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, outerRadius)
    starGradient.addColorStop(0, 'rgba(255, 255, 255, 1)')
    starGradient.addColorStop(0.6, 'rgba(255, 255, 255, 0.9)')
    starGradient.addColorStop(1, 'rgba(255, 255, 255, 0.7)')

    ctx.fillStyle = starGradient
    ctx.fill()

    const texture = new THREE.CanvasTexture(canvas)
    texture.needsUpdate = true
    return texture
  }

  private getOptimalParticleCount(): number {
    // PRD v2: Hard cap under 1000, reduced counts
    const screenArea = window.innerWidth * window.innerHeight
    const referenceArea = 1920 * 1080
    const screenScale = Math.sqrt(screenArea / referenceArea)

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

    let baseCount: number
    if (isMobile) {
      baseCount = 300
    } else {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl2') || canvas.getContext('webgl')
      if (gl) {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
        if (debugInfo) {
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
          if (renderer.includes('Intel') || renderer.includes('Mali') || renderer.includes('Adreno')) {
            baseCount = 500
          } else {
            baseCount = 700
          }
        } else {
          baseCount = 700
        }
      } else {
        baseCount = 700
      }
    }

    // Scale by screen size, hard cap at 940 per PRD v2
    return Math.max(200, Math.min(940, Math.round(baseCount * screenScale)))
  }

  private initParticles(): void {
    const width = window.innerWidth
    const height = window.innerHeight

    for (let i = 0; i < this.PARTICLE_COUNT; i++) {
      this.particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        life: Math.random(),
        maxLife: 0.5 + Math.random() * 0.5,
        captured: false,
        fadeTimer: 0
      })
    }
  }

  private updateGeometry(gravityWells: GravityWell[] = []): void {
    const positions = new Float32Array(this.PARTICLE_COUNT * 3)
    const colors = new Float32Array(this.PARTICLE_COUNT * 3)
    const sizes = new Float32Array(this.PARTICLE_COUNT)

    const width = window.innerWidth
    const height = window.innerHeight
    const activeWells = gravityWells.filter(w => w.isActive())

    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i]
      if (!p) continue

      positions[i * 3] = p.x - width / 2
      positions[i * 3 + 1] = -(p.y - height / 2)
      positions[i * 3 + 2] = 0

      // Glow intensity and size from nearby wells
      let glowIntensity = 0
      let sizeMultiplier = 1.0
      for (const well of activeWells) {
        const wellGlow = well.getGlowIntensity(p.x, p.y)
        glowIntensity = Math.max(glowIntensity, wellGlow)

        // Size increases dramatically near core
        const dist = well.getDistance(p.x, p.y)
        const R = well.surfaceRadius
        if (dist < R * 0.5) {
          // Core zone: large particles (bloom effect)
          sizeMultiplier = Math.max(sizeMultiplier, 2.5 + (1 - dist / (R * 0.5)) * 1.5)
        } else if (dist < R * 1.2) {
          // Disk zone: medium-large particles
          sizeMultiplier = Math.max(sizeMultiplier, 1.5 + (1 - dist / (R * 1.2)) * 1.0)
        } else if (dist < R * 2.5) {
          // Orbit zone: slightly larger
          sizeMultiplier = Math.max(sizeMultiplier, 1.0 + (1 - dist / (R * 2.5)) * 0.5)
        }
      }
      sizes[i] = sizeMultiplier

      // Speed-based color
      const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy)
      const speedNorm = Math.min(speed / this.BASE_MAX_VELOCITY, 1)

      let r: number, g: number, b: number
      if (speedNorm < 0.3) {
        r = 0.3 + speedNorm
        g = 0.2 + speedNorm * 0.5
        b = 0.9
      } else if (speedNorm < 0.7) {
        const t = (speedNorm - 0.3) / 0.4
        r = 0.6 + t * 0.4
        g = 0.5 + t * 0.5
        b = 0.9 - t * 0.2
      } else {
        const t = (speedNorm - 0.7) / 0.3
        r = 1.0
        g = 1.0 - t * 0.3
        b = 0.7 - t * 0.4
      }

      const proximityBoost = 1 + glowIntensity * 3
      let brightness = (0.7 + p.life * 0.5) * proximityBoost

      // Fade out captured particles
      if (p.captured) {
        const fadeAlpha = Math.max(0, p.fadeTimer / 0.5)
        brightness *= fadeAlpha
      }

      colors[i * 3] = Math.min(r * brightness, 2.0)
      colors[i * 3 + 1] = Math.min(g * brightness, 2.0)
      colors[i * 3 + 2] = Math.min(b * brightness, 2.0)
    }

    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

    if (this.geometry.attributes['position']) {
      this.geometry.attributes['position'].needsUpdate = true
    }
    if (this.geometry.attributes['color']) {
      this.geometry.attributes['color'].needsUpdate = true
    }

    // Update material size based on proximity to wells (guard for first call)
    if (this.material) {
      if (activeWells.length > 0) {
        const maxSize = Math.max(...sizes)
        this.material.size = 38 * Math.max(1, maxSize * 0.5)
      } else {
        this.material.size = 38
      }
    }
  }

  public update(gravityWells: GravityWell[]): void {
    const now = performance.now() / 1000
    const rawDt = now - this.lastTime
    this.lastTime = now

    // Cap dt to prevent physics explosion on tab switch
    const dt = Math.min(rawDt, 1 / 30)

    const width = window.innerWidth
    const height = window.innerHeight
    const activeWells = gravityWells.filter(w => w.isActive())

    // Frame-rate independent damping factor
    const dampingFactor = Math.exp(-this.DAMPING_PER_SEC * dt)

    // Track max velocity for debug
    let maxVel = 0
    let totalVel = 0

    // Reset zone counts
    const zoneCounts = { far: 0, orbit: 0, disk: 0, core: 0 }

    for (const p of this.particles) {
      // Failsafe: respawn any particle that escaped far off-screen
      if (p.x < -this.SPAWN_MARGIN || p.x > width + this.SPAWN_MARGIN ||
          p.y < -this.SPAWN_MARGIN || p.y > height + this.SPAWN_MARGIN) {
        this.respawnParticle(p)
      }

      // Track closest zone for this particle
      let closestZone: 'far' | 'orbit' | 'disk' | 'core' = 'far'
      let closestDist = Infinity

      // Apply gravity from each well using Plummer softening
      for (const well of activeWells) {
        const dx = well.x - p.x
        const dy = well.y - p.y
        const distSq = dx * dx + dy * dy
        const dist = Math.sqrt(distSq)

        if (dist < 1) continue

        // Direction toward well
        const nx = dx / dist
        const ny = dy / dist

        // Calculate angle from well to particle for shaped rings
        const angle = Math.atan2(-dy, -dx) // Angle from well's perspective

        // Zone boundaries for this well (shaped by ring type)
        const baseR = well.surfaceRadius
        const R = this.getShapedRadius(baseR, angle)
        const coreRadius = this.CORE_RADIUS_FACTOR * R
        const diskInner = R - this.DISK_WIDTH_FACTOR * R
        const diskOuter = R + this.DISK_WIDTH_FACTOR * R
        const orbitOuter = this.ORBIT_OUTER_FACTOR * R

        // Track zone (use closest well)
        if (dist < closestDist) {
          closestDist = dist
          if (dist <= coreRadius) {
            closestZone = 'core'
          } else if (dist <= diskOuter && dist >= diskInner) {
            closestZone = 'disk'
          } else if (dist <= orbitOuter) {
            closestZone = 'orbit'
          } else {
            closestZone = 'far'
          }
        }

        // Plummer softening: prevents singularity and snap-in
        // a = G * m / (r² + ε²)^(3/2) * r
        const epsilon = Math.max(18, 0.35 * baseR)
        const softDistSq = distSq + epsilon * epsilon
        const softDist = Math.sqrt(softDistSq)

        // Gravitational acceleration (Plummer-softened)
        let accel = this.GRAVITY_STRENGTH * well.mass / (softDist * softDist * softDist) * dist

        // Inside disk outer: modified gravity for ring formation
        if (dist <= diskOuter) {
          if (dist <= coreRadius) {
            // Core: normal gravity - particles get captured here
            accel *= 0.8
          } else if (dist < R) {
            // Between core and R: push outward (repulsive) - keeps ring stable
            // Weaker near core boundary to allow occasional capture
            const pushStrength = 0.5 * (dist - coreRadius) / (R - coreRadius)
            accel *= -pushStrength
          } else {
            // Between R and diskOuter: weak pull inward
            accel *= 0.3
          }
        }

        // Cap acceleration to prevent sudden jumps
        const maxAccel = Math.min(60, 2.5 * baseR)
        const cappedAccel = Math.min(accel, maxAccel)

        // Apply radial acceleration (toward well)
        p.vx += nx * cappedAccel * dt
        p.vy += ny * cappedAccel * dt

        // Apply tangential force in orbit zone (creates orbital motion)
        if (dist > R && dist <= orbitOuter) {
          // Tangential direction: perpendicular to radial (counter-clockwise)
          const tx = -ny
          const ty = nx

          // Tangential force strength - stronger closer to disk
          const orbitProgress = (dist - R) / (orbitOuter - R)
          const tangentStrength = this.ORBIT_OMEGA * (1 - orbitProgress * 0.5)

          p.vx += tx * tangentStrength * dt * 60
          p.vy += ty * tangentStrength * dt * 60
        }

        // Disk band: heavy friction + slight speed variation to prevent clumping
        if (dist >= diskInner && dist <= diskOuter) {
          // Strong friction - particles slow down here
          p.vx *= 0.92
          p.vy *= 0.92

          // Add small random tangential variation to spread particles uniformly
          // This gives each particle slightly different orbital speed
          const tx = -ny
          const ty = nx
          const variation = (Math.random() - 0.5) * 0.12 * dt * 60
          p.vx += tx * variation
          p.vy += ty * variation
        }
      }

      // Count zone
      zoneCounts[closestZone]++

      // Core capture: particles in core start fading
      if (closestZone === 'core' && !p.captured) {
        p.captured = true
        p.fadeTimer = 0.5 // 0.5 second fade
      }

      // Handle captured particle fade-out
      if (p.captured) {
        p.fadeTimer -= dt
        if (p.fadeTimer <= 0) {
          this.respawnParticle(p)
        }
        // Skip physics for captured particles - they just fade
        continue
      }

      // Apply damping (frame-rate independent)
      p.vx *= dampingFactor
      p.vy *= dampingFactor

      // Drift mode when no hands
      if (this.driftMode && activeWells.length === 0) {
        this.driftAngle += 0.0001
        p.vx += Math.cos(this.driftAngle) * 0.02 * dt * 60
        p.vy += Math.sin(this.driftAngle) * 0.02 * dt * 60
      }

      // Velocity cap (scaled by surface radius if available)
      const maxR = activeWells.length > 0
        ? Math.max(...activeWells.map(w => w.surfaceRadius))
        : 50
      const maxVelocity = Math.min(70, Math.max(18, 0.9 * maxR))

      const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy)
      if (speed > maxVelocity) {
        p.vx = (p.vx / speed) * maxVelocity
        p.vy = (p.vy / speed) * maxVelocity
      }

      // Track metrics
      const currentSpeed = Math.sqrt(p.vx * p.vx + p.vy * p.vy)
      maxVel = Math.max(maxVel, currentSpeed)
      totalVel += currentSpeed

      // Update position (semi-implicit Euler: velocity updated first)
      p.x += p.vx * dt * 60 // Scale for 60fps baseline
      p.y += p.vy * dt * 60

      // Respawn if out of bounds
      if (p.x < -this.SPAWN_MARGIN || p.x > width + this.SPAWN_MARGIN ||
          p.y < -this.SPAWN_MARGIN || p.y > height + this.SPAWN_MARGIN) {
        this.respawnParticle(p)
      }
    }

    // Store metrics
    this.currentMaxVelocity = maxVel
    this.currentAvgVelocity = totalVel / this.particles.length
    this.zoneCount = zoneCounts

    this.updateGeometry(gravityWells)
  }

  private respawnParticle(p: Particle): void {
    const width = window.innerWidth
    const height = window.innerHeight

    // Spawn at random edge (5-10% outside viewport per PRD v2)
    const padding = Math.max(width, height) * 0.07
    const edge = Math.floor(Math.random() * 4)

    switch (edge) {
      case 0: // Top
        p.x = Math.random() * width
        p.y = -padding
        break
      case 1: // Right
        p.x = width + padding
        p.y = Math.random() * height
        break
      case 2: // Bottom
        p.x = Math.random() * width
        p.y = height + padding
        break
      case 3: // Left
        p.x = -padding
        p.y = Math.random() * height
        break
    }

    // Initial velocity towards center
    const centerX = width / 2
    const centerY = height / 2
    const dx = centerX - p.x
    const dy = centerY - p.y
    const dist = Math.sqrt(dx * dx + dy * dy)

    p.vx = (dx / dist) * (0.5 + Math.random() * 1.5)
    p.vy = (dy / dist) * (0.5 + Math.random() * 1.5)
    p.life = p.maxLife
    p.captured = false
    p.fadeTimer = 0
  }

  public setDriftMode(enabled: boolean): void {
    this.driftMode = enabled
  }

  public getMetrics(): ParticleMetrics {
    return {
      particleCount: this.PARTICLE_COUNT,
      maxVelocity: this.currentMaxVelocity,
      avgVelocity: this.currentAvgVelocity,
      zoneDistribution: this.zoneCount
    }
  }

  public dispose(): void {
    this.geometry.dispose()
    this.material.dispose()
    this.scene.remove(this.points)
  }
}
